import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Banner, Button, Card, CardBody, CardHeader, Input, Spinner } from '../components/ui';
import {
  BaseProfileFields,
  EMPTY_BASE_PROFILE,
  validateBaseProfile,
  type BaseProfileErrors,
  type BaseProfileState,
} from '../components/onboarding/BaseProfileFields';
import { TermsAcceptanceStep } from '../components/onboarding/TermsAcceptanceStep';
import {
  acceptOrganisationInvite,
  buildInviteAuthorizeURL,
  CURRENT_TERMS_VERSION,
  dashboardURLFor,
  deriveTerritory,
  extractApiErrorCode,
  extractApiErrorMessage,
  previewOrganisationInvite,
  type AcceptOrganisationInviteBody,
  type BaseProfileBody,
  type InvitePreviewResponse,
} from '../lib/auth-onboarding';
import { buildOptionalConsents } from '../lib/consents';

// The invite JWT and the WorkOS-minted identity JWT ride in the URL fragment on
// arrival; we stash them in sessionStorage so they survive the fragment strip
// (and the profile step) but never hit the query string, a server log, or a
// referrer header.
const INVITE_TOKEN_KEY = 'org_invite_token';
const IDENTITY_TOKEN_KEY = 'org_invite_identity_token';

type Phase = 'previewing' | 'ready' | 'authenticating' | 'profile' | 'submitting' | 'redirecting' | 'fatal';

// roleLabel renders the scoped invite role in human copy.
function roleLabel(roleType: string): string {
  switch (roleType) {
    case 'org_admin':
      return 'Organisation admin';
    case 'hiring_manager':
      return 'Hiring manager';
    default:
      return roleType;
  }
}

export default function InviteJoinPage(): JSX.Element {
  const [phase, setPhase] = useState<Phase>('previewing');
  const [preview, setPreview] = useState<InvitePreviewResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const didInit = useRef(false);
  // previewRef mirrors `preview` so attemptAccept can read the freshly-fetched
  // value synchronously. The preview `.then()` fires attemptAccept in the same
  // tick it calls setPreview, so the `preview` state closure is still null there;
  // reading the ref (set alongside setPreview) is what lets the
  // AUTHENTICATION_REQUIRED branch actually navigate instead of no-op'ing.
  const previewRef = useRef<InvitePreviewResponse | null>(null);

  // attemptAccept redeems the invite with whatever credential we hold (identity
  // token from the WorkOS round-trip, else the current session cookie). It maps
  // the backend error contract onto the next UI state. When called from the
  // profile step (`fromProfileStep`), generic errors are re-thrown so the step
  // stays mounted and surfaces them inline instead of the page unmounting to a
  // fatal banner and losing the entered form.
  const attemptAccept = useCallback(
    async (
      profile?: BaseProfileBody,
      roleTitle?: string,
      opts?: { fromProfileStep?: boolean },
    ): Promise<void> => {
      const fromProfile = opts?.fromProfileStep ?? false;
      const inviteToken = sessionStorage.getItem(INVITE_TOKEN_KEY);
      if (!inviteToken) {
        setErrorMsg('This invite link is missing or has expired. Please open the link from your invitation email again.');
        setPhase('fatal');
        return;
      }
      // The profile step owns its own submitting flag; flipping page phase here
      // would unmount it. For every other caller, show the page spinner.
      if (!fromProfile) setPhase('submitting');
      setErrorMsg(null);
      const body: AcceptOrganisationInviteBody = { invite_token: inviteToken };
      const identityToken = sessionStorage.getItem(IDENTITY_TOKEN_KEY);
      if (identityToken) body.identity_token = identityToken;
      if (profile) body.profile = profile;
      if (roleTitle && roleTitle.trim()) body.role_title = roleTitle.trim();

      try {
        const res = await acceptOrganisationInvite(body);
        sessionStorage.removeItem(INVITE_TOKEN_KEY);
        sessionStorage.removeItem(IDENTITY_TOKEN_KEY);
        setPhase('redirecting');
        // Hand the fresh session to the recruiter console via the fragment.
        window.location.replace(`${dashboardURLFor('employer')}/auth/callback#token=${res.token}`);
      } catch (err) {
        const code = extractApiErrorCode(err);
        if (code === 'AUTHENTICATION_REQUIRED') {
          // We don't yet know who's redeeming — start the WorkOS invite round-trip.
          // Only the opaque invite_id travels; the invite JWT stays put. Read the
          // ref, not the `preview` closure, which is stale null on the initial
          // preview `.then()` path.
          const pv = previewRef.current;
          if (pv) {
            setPhase('authenticating');
            window.location.assign(buildInviteAuthorizeURL(pv.invite_id));
          } else {
            setErrorMsg('This invite link is missing or has expired. Please open the link from your invitation email again.');
            setPhase('fatal');
          }
          return;
        }
        if (code === 'PROFILE_REQUIRED') {
          // Already collecting a profile: surface inline rather than looping.
          if (fromProfile) throw err;
          setPhase('profile');
          return;
        }
        // Let the profile step catch and render this next to the still-mounted form.
        if (fromProfile) throw err;
        setErrorMsg(extractApiErrorMessage(err));
        setPhase('fatal');
      }
    },
    [],
  );

  useEffect(() => {
    // One-shot: didInit survives StrictMode's dev mount/unmount/remount, so the
    // preview runs exactly once. Do NOT add a `cancelled` cleanup flag here — under
    // StrictMode the first mount's cleanup would cancel the in-flight preview and the
    // remount skips (didInit), leaving the page stuck on the spinner.
    if (didInit.current) return;
    didInit.current = true;

    // 1. Lift any fragment tokens into sessionStorage, then scrub the fragment so
    //    the bearer capability never lingers in the address bar / history entry.
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const hashInvite = hashParams.get('token');
    const hashIdentity = hashParams.get('identity_token');
    if (hashInvite) sessionStorage.setItem(INVITE_TOKEN_KEY, hashInvite);
    if (hashIdentity) sessionStorage.setItem(IDENTITY_TOKEN_KEY, hashIdentity);
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    // 2. A WorkOS callback failure bounces back as ?error=…; surface it directly.
    const queryError = new URLSearchParams(window.location.search).get('error');
    if (queryError) {
      setErrorMsg('We could not verify your identity. Please try opening your invitation link again.');
      setPhase('fatal');
      return;
    }

    const inviteToken = sessionStorage.getItem(INVITE_TOKEN_KEY);
    if (!inviteToken) {
      setErrorMsg('This invite link is missing or has expired. Please open the link from your invitation email again.');
      setPhase('fatal');
      return;
    }

    previewOrganisationInvite(inviteToken)
      .then((p) => {
        previewRef.current = p;
        setPreview(p);
        // Returning from the WorkOS round-trip: we now hold an identity token, so
        // redeem immediately rather than waiting for another click.
        if (sessionStorage.getItem(IDENTITY_TOKEN_KEY)) {
          void attemptAccept();
        } else {
          setPhase('ready');
        }
      })
      .catch((err) => {
        setErrorMsg(extractApiErrorMessage(err));
        setPhase('fatal');
      });
  }, [attemptAccept]);

  if (phase === 'profile') {
    return (
      <InviteProfileStep
        preview={preview}
        onSubmit={(profile, roleTitle) => attemptAccept(profile, roleTitle, { fromProfileStep: true })}
      />
    );
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-12 sm:px-6 lg:px-8">
      <Card>
        <CardHeader title="Join your team on Reed.ai" />
        <CardBody>
          {phase === 'previewing' || phase === 'redirecting' || phase === 'authenticating' ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : phase === 'fatal' ? (
            <Banner variant="error" message={errorMsg ?? 'Something went wrong. Please try again.'} />
          ) : (
            <div className="flex flex-col gap-6">
              {preview && (
                <div className="rounded-md border border-border-tertiary bg-surface-secondary px-4 py-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-text-secondary">You've been invited to join</p>
                  <p className="mt-1 text-sm font-semibold text-text-primary">{preview.organisation_name}</p>
                  <p className="text-xs text-text-secondary">
                    as {roleLabel(preview.role_type)} · {preview.invited_email_masked}
                  </p>
                </div>
              )}
              <p className="text-sm text-text-secondary">
                Continue to verify it's you and join {preview?.organisation_name ?? 'your team'}. You'll be
                taken to our secure sign-in page.
              </p>
              {errorMsg && (
                <Banner variant="error" message={errorMsg} />
              )}
              <Button
                type="button"
                className="w-full"
                disabled={phase === 'submitting'}
                onClick={() => void attemptAccept()}
              >
                {phase === 'submitting' ? <Spinner size="sm" /> : null}
                Continue
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ── Profile step (brand-new invitee) ──────────────────────────────────────────

interface InviteProfileStepProps {
  preview: InvitePreviewResponse | null;
  onSubmit: (profile: BaseProfileBody, roleTitle?: string) => Promise<void>;
}

function InviteProfileStep({ preview, onSubmit }: InviteProfileStepProps): JSX.Element {
  const [step, setStep] = useState<'profile' | 'terms'>('profile');
  const [base, setBase] = useState<BaseProfileState>(EMPTY_BASE_PROFILE);
  const [roleTitle, setRoleTitle] = useState('');
  const [errors, setErrors] = useState<BaseProfileErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    deriveTerritory()
      .then((d) => {
        if (cancelled) return;
        setBase((prev) => ({ ...prev, territory: d.territory, territorySource: d.territory_source }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  function updateBase<K extends keyof BaseProfileState>(key: K, next: BaseProfileState[K]): void {
    setBase((prev) => ({ ...prev, [key]: next }));
  }



  function handleContinue(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const v = validateBaseProfile(base);
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    setSubmitError(null);
    setStep('terms');
  }

  async function handleSubmit(): Promise<void> {
    const body: BaseProfileBody = {
      territory: base.territory,
      territory_source: base.territorySource,
      phone: base.phone,
      postcode: base.postcode,
      accept_terms_version: CURRENT_TERMS_VERSION,
      // Invite-join follows the employer pattern (frame E-Invite): required
      // T&Cs tick-box, marketing opt-out, no WhatsApp/mobile consent.
      optional_consents: buildOptionalConsents({
        audience: 'employer',
        marketingOptOut: base.marketingOptOut,
      }),
      ...(base.county.trim() ? { county: base.county.trim() } : {}),
    };
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(body, roleTitle);
      // On success onSubmit redirects; if it returns (e.g. mapped error), re-enable.
      setSubmitting(false);
    } catch (err) {
      setSubmitError(extractApiErrorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8">
      {step === 'profile' ? (
        <Card>
          <CardHeader
            title="Almost there — a few more details"
            trailing={<span className="text-sm text-text-secondary">Step 1 of 2</span>}
          />
          <CardBody>
            <p className="mb-6 text-sm text-text-secondary">
              Add your contact details to finish joining {preview?.organisation_name ?? 'your team'}.
            </p>
            <form className="flex flex-col gap-5" onSubmit={handleContinue} noValidate>
              <Input
                label="Your role (optional)"
                placeholder="e.g. Head of Talent"
                value={roleTitle}
                disabled={submitting}
                onChange={(e) => setRoleTitle(e.target.value)}
              />
              <BaseProfileFields
                value={base}
                errors={errors}
                audience="employer"
                disabled={submitting}
                onChange={updateBase}
              />
              <Button type="submit" className="w-full">
                Continue
              </Button>
            </form>
          </CardBody>
        </Card>
      ) : (
        <TermsAcceptanceStep
          audience="employer"
          accepted={base.acceptedTerms}
          marketingOptOut={base.marketingOptOut}
          onToggleAccepted={(next) => updateBase('acceptedTerms', next)}
          onToggleMarketingOptOut={(next) => updateBase('marketingOptOut', next)}
          onBack={() => setStep('profile')}
          onSubmit={() => void handleSubmit()}
          submitting={submitting}
          submitError={submitError}
        />
      )}
    </div>
  );
}
